import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Search, Trash2, X, PlayCircle, Info } from "lucide-react";

const CATEGORIES = ["Strength", "Olympic", "Bodyweight", "Plyometrics", "Conditioning", "Mobility"];

function NewExerciseModal({ open, onClose, onSaved }) {
    const [form, setForm] = useState({ name: "", category: "Strength", primary_muscle: "", equipment: "", description: "", video_url: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            const { data } = await api.post("/exercises", form);
            onSaved(data); onClose();
            setForm({ name: "", category: "Strength", primary_muscle: "", equipment: "", description: "", video_url: "" });
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="exercise-modal">
            <div className="bg-[var(--surface)] border border-[var(--border-bright)] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <h3 className="font-serif text-3xl">New exercise</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <label className="label-tiny block mb-2">Name</label>
                        <input required className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="exercise-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-tiny block mb-2">Category</label>
                            <select className="input-dark" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Primary muscle</label>
                            <input className="input-dark" value={form.primary_muscle} onChange={(e) => setForm({ ...form, primary_muscle: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Equipment</label>
                        <input className="input-dark" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} />
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Description / coaching cues</label>
                        <textarea className="input-dark min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Bar on upper traps. Brace, descend between hips, drive knees out." />
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Demo video URL (YouTube, Instagram, etc.)</label>
                        <input className="input-dark" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/…" />
                    </div>
                    {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-primary" disabled={busy} data-testid="save-exercise-btn">
                            {busy ? "Saving…" : "Add to library"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ExerciseCard({ ex, onRemove, onUpdated }) {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ description: ex.description || "", video_url: ex.video_url || "" });
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put(`/exercises/${ex.id}`, {
                name: ex.name, category: ex.category, primary_muscle: ex.primary_muscle || "",
                equipment: ex.equipment || "", description: form.description, video_url: form.video_url,
            });
            onUpdated(data);
            setEditing(false);
        } finally { setSaving(false); }
    };

    return (
        <div className="p-4 border-b border-r border-[var(--border)]" data-testid={`exercise-${ex.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="font-medium">{ex.name}</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1 font-mono truncate">{ex.primary_muscle || "—"} · {ex.equipment || "—"}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setExpanded(!expanded)} className="text-[var(--text-muted)] hover:text-[var(--gold)]" title="Show details" data-testid={`ex-info-${ex.id}`}>
                        <Info size={13} />
                    </button>
                    {ex.video_url && (
                        <a href={ex.video_url} target="_blank" rel="noreferrer" className="text-[var(--gold)] hover:text-[var(--gold-bright)]" title="Demo" data-testid={`ex-demo-${ex.id}`}>
                            <PlayCircle size={14} />
                        </a>
                    )}
                    {ex.coach_id !== "system" && onRemove && (
                        <button onClick={() => onRemove(ex.id)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><Trash2 size={13} /></button>
                    )}
                </div>
            </div>
            {expanded && !editing && (
                <div className="mt-3 text-xs text-[var(--text-muted)] border-l-2 border-[var(--gold-deep)] pl-3 space-y-2">
                    <p>{ex.description || "No description yet."}</p>
                    <button onClick={() => setEditing(true)} className="text-[var(--gold)] hover:text-[var(--gold-bright)] font-mono tracking-wider uppercase text-[10px]" data-testid={`ex-edit-${ex.id}`}>
                        Edit description / demo →
                    </button>
                </div>
            )}
            {editing && (
                <form onSubmit={save} className="mt-3 space-y-2 border-l-2 border-[var(--gold)] pl-3" data-testid={`ex-edit-form-${ex.id}`}>
                    <textarea className="input-dark text-xs min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Coaching cues…" />
                    <input className="input-dark text-xs" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="Demo URL" />
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setEditing(false)} className="btn-ghost text-[10px] flex-1">Cancel</button>
                        <button type="submit" disabled={saving} className="btn-primary text-[10px] flex-1" data-testid={`ex-save-${ex.id}`}>{saving ? "…" : "Save"}</button>
                    </div>
                </form>
            )}
        </div>
    );
}

export default function ExercisesPage() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [open, setOpen] = useState(false);

    const load = async () => {
        const params = {};
        if (cat) params.category = cat;
        if (q) params.q = q;
        const { data } = await api.get("/exercises", { params });
        setItems(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, q]);

    const grouped = useMemo(() => {
        const g = {};
        for (const ex of items) (g[ex.category] ||= []).push(ex);
        return g;
    }, [items]);

    const removeMine = async (id) => {
        await api.delete(`/exercises/${id}`);
        setItems(items.filter((i) => i.id !== id));
    };

    return (
        <div className="p-8 space-y-6" data-testid="exercises-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="label-tiny mb-2">/ Library</p>
                    <h1 className="font-serif text-6xl">Exercises</h1>
                </div>
                <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="new-exercise-btn">
                    <Plus size={14} /> New exercise
                </button>
            </div>

            <div className="flex gap-3 flex-wrap">
                <div className="flex-1 flex items-center gap-3 border border-[var(--border)] bg-[var(--surface)] px-4 py-2 min-w-[280px]">
                    <Search size={14} className="text-[var(--text-muted)]" />
                    <input className="bg-transparent outline-none flex-1 text-sm tracking-wider uppercase" placeholder="SEARCH NAME" value={q} onChange={(e) => setQ(e.target.value)} data-testid="exercises-search" />
                </div>
                <select className="input-dark w-48" value={cat} onChange={(e) => setCat(e.target.value)}>
                    <option value="">ALL CATEGORIES</option>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
            </div>

            {Object.entries(grouped).map(([category, list]) => (
                <div key={category} className="border border-[var(--border)] bg-[var(--surface)]">
                    <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
                        <h3 className="font-display text-xl tracking-[0.18em]">{category}</h3>
                        <span className="font-mono text-xs text-[var(--text-muted)]">{list.length}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                        {list.map((ex) => (
                            <ExerciseCard
                                key={ex.id}
                                ex={ex}
                                onRemove={ex.coach_id !== "system" ? removeMine : null}
                                onUpdated={(upd) => setItems(items.map((i) => (i.id === upd.id ? upd : (i.id === ex.id && upd.cloned_from === ex.id ? upd : i))))}
                            />
                        ))}
                    </div>
                </div>
            ))}

            <NewExerciseModal open={open} onClose={() => setOpen(false)} onSaved={(item) => setItems([item, ...items])} />
        </div>
    );
}
